/**
 * Listens on `calls/{callSid}` and notifies the view of status changes. The
 * legacy `calling-screen.js::trackCallStatus` had multiple cleanup paths and
 * could leak listeners — here we guarantee a single subscription per call.
 */

import { ref } from '../../core/firebase.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('call-tracker');

/** @typedef {'initiated' | 'queued' | 'ringing' | 'in-progress' | 'answered' | 'completed' | 'no-answer' | 'busy' | 'failed' | 'canceled'} CallStatus */

const TERMINAL = new Set(['completed', 'no-answer', 'busy', 'failed', 'canceled']);

const MAX_LIFETIME_MS = 5 * 60 * 1000;

/** @type {{ stop: () => void } | null} */
let active = null;

/**
 * Start tracking. Returns a stop function. Calling track() while another is
 * active stops the previous run first.
 *
 * @param {string} callSid
 * @param {(state: { status: CallStatus | string; data: any }) => void} onChange
 * @returns {() => void}
 */
export function track(callSid, onChange) {
  if (active) active.stop();
  if (!callSid) return () => {};

  const r = ref(`calls/${callSid}`);
  /** @type {ReturnType<typeof setTimeout>} */
  let timeout;
  let stopped = false;

  const cb = (/** @type {any} */ snap) => {
    if (stopped) return;
    const data = snap.val() ?? {};
    const status = String(data.status ?? 'initiated');
    try { onChange({ status, data }); }
    catch (e) { log.warn('onChange threw:', e); }
    if (TERMINAL.has(status)) stop();
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    try { r.off('value', cb); } catch { /* ignore */ }
    clearTimeout(timeout);
    if (active && active.stop === stop) active = null;
  };

  r.on('value', cb, (/** @type {Error} */ err) => log.warn('listener err:', err.message));

  // Hard timeout so a stuck listener can't live forever.
  timeout = setTimeout(() => {
    log.warn('tracker timeout — forcing stop');
    stop();
  }, MAX_LIFETIME_MS);

  active = { stop };
  return stop;
}

export function stopActive() { if (active) active.stop(); }
